/**
 * The intoxication model: booze (movement response, grip and camera accents)
 * and acid (the psychedelic render layer).
 *
 * ------------------------------------------------------------------------
 *  DETERMINISM CONTRACT
 * ------------------------------------------------------------------------
 *  `sample(t)` is a *pure function of the run's simulation time and the current
 *  dose*. It holds no per-run state, never reads the wall clock, never touches
 *  Math.random() and never depends on the frame rate. Two runs at the same dose
 *  that reach t = 12.5 s see exactly the same numbers, which is what makes the
 *  game raceable.
 *
 *  The starting dose identifies the run. Successful sips update the live dose
 *  in RunSimulation before movement; replay inputs reproduce those updates.
 * ------------------------------------------------------------------------
 */

import {
  INTOXICATION,
  PSYCHEDELIC,
  REFERENCE_INTOXICATION,
  STUMBLE_SCHEDULE,
  alcoholFactor,
  drugFactor,
  sanitiseLevel,
  type IntoxicationConfig,
  type IntoxicationLevel,
  type PsychedelicConfig,
  type StumbleEvent,
  type SwayComponent,
} from '../config/intoxication';
import { normalize2 } from '../utils/math';

/** Scalar drives handed to the psychedelic render pass. */
export interface PsychedelicSample {
  /** 0..1, the raw dose. 0 means "skip the whole pass". */
  intensity: number;
  warp: number;
  chroma: number;
  /** Global hue rotation, radians. */
  hue: number;
  hueRadial: number;
  saturation: number;
  /** Radial zoom pulse, fraction. */
  breathe: number;
  /** Whole-screen swirl, radians. */
  swirl: number;
  ripple: number;
}

export interface IntoxicationSample {
  /** Input response duration multiplier; grows with dose even after grip bottoms out. */
  responseTimeScale: number;
  /** Jump button latency in simulation seconds, independent of run time. */
  jumpDelay: number;
  /** Ground friction and acceleration multiplier, independent of run time. */
  groundGrip: number;
  /** 0..1, strength of the cosmetic scuff (debug / audio). */
  stumbleIntensity: number;

  cameraYaw: number;
  cameraPitch: number;
  cameraRoll: number;
  /** Head bob, still needs to be scaled by the player's actual speed. */
  bobLateral: number;
  bobVertical: number;

  psychedelic: PsychedelicSample;
}

function evaluateSway(components: readonly SwayComponent[], t: number): number {
  let sum = 0;
  for (const c of components) {
    sum += Math.sin(t * c.freq + c.phase) * c.amp;
  }
  return sum;
}

/** Smooth 0 -> 1 -> 0 envelope over the life of a stumble. */
function envelope(u: number): number {
  if (u <= 0 || u >= 1) return 0;
  return Math.sin(Math.PI * u);
}

export class IntoxicationSystem {
  private readonly schedule: StumbleEvent[];
  private level: IntoxicationLevel;
  private alcohol: number;
  private drugs: number;

  constructor(
    schedule: readonly StumbleEvent[] = STUMBLE_SCHEDULE,
    level: IntoxicationLevel = REFERENCE_INTOXICATION,
    private readonly config: IntoxicationConfig = INTOXICATION,
    private readonly acid: PsychedelicConfig = PSYCHEDELIC,
  ) {
    // Normalise directions once so the level designer can write {x: 1, y: 0.3}.
    this.schedule = schedule
      .map((e) => ({ ...e, direction: normalize2(e.direction) }))
      .sort((a, b) => a.startTime - b.startTime);
    this.level = sanitiseLevel(level);
    this.alcohol = alcoholFactor(this.level);
    this.drugs = drugFactor(this.level);
  }

  /** Set the live dose from simulation, or the starting dose on reset. */
  setLevel(level: IntoxicationLevel): void {
    this.level = sanitiseLevel(level);
    this.alcohol = alcoholFactor(this.level);
    this.drugs = drugFactor(this.level);
  }

  get currentLevel(): IntoxicationLevel {
    return this.level;
  }

  /** How hard the booze is hitting. 1.0 = the authored reference feel. */
  get alcoholStrength(): number {
    return this.alcohol;
  }

  get drugStrength(): number {
    return this.drugs;
  }

  /** The cosmetic schedule, for audio, debug and tests. */
  get stumbles(): readonly StumbleEvent[] {
    return this.schedule;
  }

  /** Time (seconds) until the next cosmetic scuff, or Infinity. */
  timeToNextStumble(t: number): number {
    if (this.alcohol <= 0) return Infinity;
    for (const e of this.schedule) {
      if (e.startTime > t) return e.startTime - t;
    }
    return Infinity;
  }

  sample(t: number): IntoxicationSample {
    const move = this.config.movement;
    const cam = this.config.camera;

    const camScale = this.alcohol * cam.scale;

    const sample: IntoxicationSample = {
      responseTimeScale: 1 + this.alcohol * move.responseSlowdownPerReference,
      jumpDelay: this.alcohol * move.jumpDelayPerReference,
      groundGrip: Math.max(move.minGrip, 1 - this.alcohol * move.gripLossPerReference),
      stumbleIntensity: 0,
      cameraYaw: evaluateSway(cam.yaw, t) * camScale,
      cameraPitch: evaluateSway(cam.pitch, t) * camScale,
      cameraRoll: evaluateSway(cam.roll, t) * camScale,
      bobLateral: Math.sin(t * cam.bobFrequency) * cam.bobLateral * camScale,
      bobVertical: Math.abs(Math.sin(t * cam.bobFrequency * 0.5)) * cam.bobVertical * camScale,
      psychedelic: this.samplePsychedelic(t),
    };

    if (this.alcohol > 0) {
      // The schedule is sorted, so we can stop as soon as we pass `t`.
      for (const e of this.schedule) {
        const leadStart = e.startTime - cam.stumbleLead;
        if (leadStart > t) break;
        const end = e.startTime + e.duration;
        if (end <= t) continue;

        const env = envelope((t - e.startTime) / e.duration) * e.strength;
        if (env > 0) {
          sample.stumbleIntensity = Math.max(sample.stumbleIntensity, env);
        }

        // Camera and scuff are presentation only; neither changes ground grip.
        const leadEnv = envelope((t - leadStart) / (e.duration + cam.stumbleLead)) * e.strength;
        sample.cameraRoll -= e.direction.x * leadEnv * cam.stumbleRoll * camScale;
        sample.cameraPitch += e.direction.y * leadEnv * cam.stumbleRoll * 0.25 * camScale;
      }
    }

    // Acid drifts the view but never the feet.
    if (this.drugs > 0) {
      const f = this.acid.cameraDriftFrequency;
      sample.cameraYaw += Math.sin(t * f) * this.acid.cameraDriftYaw * this.drugs;
      sample.cameraPitch += Math.sin(t * f * 1.37 + 2.1) * this.acid.cameraDriftPitch * this.drugs;
    }

    return sample;
  }

  private samplePsychedelic(t: number): PsychedelicSample {
    const d = this.drugs;
    if (d <= 0) {
      return {
        intensity: 0,
        warp: 0,
        chroma: 0,
        hue: 0,
        hueRadial: 0,
        saturation: 0,
        breathe: 0,
        swirl: 0,
        ripple: 0,
      };
    }
    const a = this.acid;
    return {
      intensity: d,
      warp: a.warp * d,
      chroma: a.chroma * d,
      hue: t * a.hueSpeed * d,
      hueRadial: a.hueRadial * d,
      saturation: a.saturation * d,
      breathe: Math.sin(t * a.breatheFrequency * Math.PI * 2) * a.breatheAmplitude * d,
      swirl: Math.sin(t * a.swirlFrequency) * a.swirl * d,
      ripple: a.ripple * d,
    };
  }
}
